// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useState, useRef, useEffect } from 'react';
import { AdminData, adminDataTemplate, ContextWrapper } from '../../../context/ContextWrapper';
import { Button, Col, Row, ScreenReaderOnly } from '@op/opds.opux';
import { IconAdd } from '@op/opds.opux-icons/action';
import AdminInformationForm from './AdminInformationForm';
import message from '../../../messages';
import UseWindowWidth from '../../../hooks/useWindowWidth';
import { deepCopy } from '../../../util/Util';
import { MAX_ADMINISTRATORS } from '../../../Constants';

const AdminInformation: React.FC = () => {
    const { contractData, updateInformation } = useContext(ContextWrapper);
    const viewThreshold = 540;
    const width = UseWindowWidth();
    const [srAnnouncement, setSrAnnouncement] = useState<string>('');
    const adminFormRefs = useRef<(HTMLDivElement | null)[]>([]);
    const previousAdminCount = useRef<number>(contractData.administrators.length);

    // Update refs array when admin count changes
    useEffect(() => {
        adminFormRefs.current = adminFormRefs.current.slice(0, contractData.administrators.length);
    }, [contractData.administrators.length]);

    // Clear screen reader announcement after timeout
    useEffect(() => {
        if (srAnnouncement) {
            const timer = setTimeout(() => setSrAnnouncement(''), 1000);
            return () => clearTimeout(timer);
        }
    }, [srAnnouncement]);

    // Scroll to newly added admin form
    useEffect(() => {
        const currentCount = contractData.administrators.length;
        const wasAdded = currentCount > previousAdminCount.current;

        if (wasAdded && adminFormRefs.current.length > 0) {
            const lastFormRef = adminFormRefs.current[currentCount - 1];
            if (lastFormRef) {
                lastFormRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Focus first input
                const firstInput = lastFormRef.querySelector('input');
                firstInput?.focus();
            }
        }

        previousAdminCount.current = currentCount;
    }, [contractData.administrators.length]);

    const modifyState = (adminInfo: AdminData, index: number) => {
        updateInformation((state: any) => {
            const copyOfState = deepCopy(state);
            copyOfState.administrators[index] = adminInfo;

            return {
                ...state,
                administrators: copyOfState.administrators,
            };
        });
    };

    const addAdminForm = () => {
        if (contractData.administrators.length >= MAX_ADMINISTRATORS) {
            return;
        }
        const copyOfContractData = deepCopy(contractData);
        const newAdmin: AdminData = adminDataTemplate(contractData.showAgreementTypes);
        copyOfContractData.administrators.push(newAdmin);
        updateInformation(copyOfContractData);
    };

    const removeForm = (data: any, index: number) => {
        const copyOfContractData = deepCopy(data);
        copyOfContractData.administrators.splice(index, 1);
        updateInformation(copyOfContractData);

        // Announce to screen readers
        setSrAnnouncement(message('adminDeletedAnnouncement'));
    };

    return (
        <>
            <ScreenReaderOnly aria-live="polite">{srAnnouncement}</ScreenReaderOnly>
            {contractData.administrators.map((admin: AdminData, index: number) => (
                <AdminInformationForm
                    key={admin.id}
                    ref={(el) => (adminFormRefs.current[index] = el)}
                    admin={admin}
                    updateInformation={(adminInfo: AdminData) => modifyState(adminInfo, index)}
                    enableDeleteButton={contractData.administrators.length > 1}
                    removeForm={() => removeForm(contractData, index)}
                />
            ))}
            {width > viewThreshold ? (
                <Row
                    center
                    className="opux-middle-sm opux-middle-xs opux-spacing-top-margin-double
                      opux-spacing-bottom-margin-double"
                    data-testid="adminInfoRow"
                >
                    <Col sm={6} xs={5} className="admin-information-left-padding">
                        <h3 data-testid="adminInfoRowH3">{message('addAdmin')}</h3>
                        {contractData.administrators.length >= MAX_ADMINISTRATORS && (
                            <p className="opux-text-body-small" data-testid="maxAdminReachedMessage">
                                {message('maxAdminReachedMessage')}
                            </p>
                        )}
                    </Col>
                    <Col
                        sm={6}
                        xs={6}
                        xsOffset={1}
                        smOffset={0}
                        className="admin-information-left"
                        data-testid="adminInfoRow"
                    >
                        <Button
                            icon={IconAdd}
                            iconLabel={message('addAdmin')}
                            onClick={addAdminForm}
                            disabled={contractData.administrators.length >= MAX_ADMINISTRATORS}
                            data-testid="adminInfoRowButton"
                        />
                    </Col>
                </Row>
            ) : (
                <>
                    <h3
                        className="opux-align-center opux-spacing-top-margin-double opux-spacing-bottom-margin-half"
                        data-testid="addAdminH3"
                    >
                        {message('addAdmin')}
                    </h3>
                    {contractData.administrators.length >= MAX_ADMINISTRATORS && (
                        <p
                            className="opux-align-center opux-text-body-small opux-spacing-bottom-margin-half"
                            data-testid="maxAdminReachedMessage"
                        >
                            {message('maxAdminReachedMessage')}
                        </p>
                    )}
                    <div className="opux-align-center opux-spacing-bottom-margin-double" data-testid="addAdmin">
                        <Button
                            icon={IconAdd}
                            iconLabel={message('addAdmin')}
                            minified
                            onClick={addAdminForm}
                            disabled={contractData.administrators.length >= MAX_ADMINISTRATORS}
                            data-testid="adminInfoRow"
                        />
                    </div>
                </>
            )}
        </>
    );
};
export default AdminInformation;
